import { WarningIcon } from "@phosphor-icons/react/dist/csr/Warning";
import { Alert, AlertDescription } from "@volca/ui/components/ui/alert";
import { Button } from "@volca/ui/components/ui/button";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import type { PayrollStatementNotificationRecipient } from "./types";
import { getChangedPacksSummary } from "./utils";

/**
 * The packs already emailed that no longer describe anybody's pay.
 *
 * A rerun or a single edited cell can move pay that has already gone out, and until
 * this banner the only way to find out whose was to open every row and compare it
 * against a figure nobody had kept. So managers either resent the whole period —
 * emailing everybody a pack identical to the one they already had — or resent nobody
 * and left technicians reading a number that was no longer their pay.
 *
 * Selects rather than sends. It hands the existing Send flow exactly this set, so the
 * confirmation step, the note drafts, and the failure reporting are all the ones the
 * manager already knows, and nothing goes out without the same click as always.
 */
export const ChangedPacksBanner = ({
  changedRecipients,
  blockedRecipients = [],
  onSelect,
  disabled,
}: {
  changedRecipients: PayrollStatementNotificationRecipient[];
  /**
   * Changed packs that cannot be sent — no saved address, or a pack that no longer
   * projects. Named rather than dropped: their rows are marked like any other changed
   * pack, so leaving them out of both the count and the copy left a mark on the page
   * that nothing accounted for.
   */
  blockedRecipients?: PayrollStatementNotificationRecipient[];
  onSelect: () => void;
  /** Off the email rollout there is nothing to resend and no button to press. */
  disabled?: boolean;
}) => {
  const count = changedRecipients.length;
  const { headline, detail } = getChangedPacksSummary({
    changedRecipients,
    blockedRecipients,
  });
  if (!headline) return null;

  return (
    <Alert color="warning" role="status" className="w-full">
      <WarningIcon />
      <AlertDescription>
        <Stack
          direction="row"
          align="center"
          justify="between"
          gap={4}
          className="w-full min-w-0 flex-wrap gap-y-2"
        >
          <Stack gap={1} align="stretch" className="min-w-0">
            <Text variant="caption" className="font-semibold">
              {headline}
            </Text>
            {detail && (
              <Text variant="caption" tone="muted">
                {detail}
              </Text>
            )}
          </Stack>
          {!disabled && count > 0 && (
            <Button
              size="sm"
              variant="secondary"
              className="ml-auto"
              onClick={onSelect}
            >
              {count === 1 ? "Select them" : `Select these ${count}`}
            </Button>
          )}
        </Stack>
      </AlertDescription>
    </Alert>
  );
};
